import { createContext, useMemo, ReactNode } from 'react';
import { StatusMessage } from 'app/agriculture/routes/Devices/utils/status/types';
import { usePowerStatus } from 'app/agriculture/routes/Devices/utils/status/powerStatus/usePowerStatus';
import { useWeatherStatus } from 'app/agriculture/routes/Devices/utils/status/weatherStatus/useWeatherStatus';
import { useDevice } from 'app/agriculture/queries/devices/useDevice';
import { useTankSensorStatus } from 'app/agriculture/routes/Devices/utils/status/tankSensorStatus/useTankSensorStatus';
import { useLocatorStatus } from 'app/agriculture/routes/Devices/utils/status/locatorStatus/useLocatorStatus';
import { useHeartbeatStatus } from 'app/agriculture/routes/Devices/utils/status/heartbeatStatus/useHeartbeatStatus';
import { aggregateStatusMessages } from 'app/agriculture/routes/Devices/utils/status/aggregateStatusMessages';
import { useNetworkStatus } from 'app/agriculture/routes/Devices/utils/status/networkStatus/useNetworkStatus';

export const DeviceStatusContext = createContext<StatusMessage>({
    status: 'unknown',
    messages: [],
});

export function DeviceStatusProvider({
    deviceId,
    children,
}: {
    deviceId: string;
    children: ReactNode;
}) {
    const device = useDevice({ deviceId });

    if (device.deviceType === 'Weather Station') {
        return (
            <WeatherStationStatusProvider deviceId={deviceId}>
                {children}
            </WeatherStationStatusProvider>
        );
    }

    if (device.deviceType === 'Level Monitor') {
        return <TankSensorStatusProvider deviceId={deviceId}>{children}</TankSensorStatusProvider>;
    }

    if (device.deviceType === 'Personal Locator Beacon') {
        return <LocatorStatusProvider deviceId={deviceId}>{children}</LocatorStatusProvider>;
    }

    return (
        <DefaultDeviceStatusProvider deviceId={deviceId}>{children}</DefaultDeviceStatusProvider>
    );
}

function DefaultDeviceStatusProvider({
    deviceId,
    children,
}: {
    deviceId: string;
    children: ReactNode;
}) {
    const heartbeatStatus = useHeartbeatStatus({ deviceId });
    const networkStatus = useNetworkStatus({ deviceId });
    const powerStatus = usePowerStatus({ deviceId });

    const status = useMemo(
        () => aggregateStatusMessages([heartbeatStatus, networkStatus, powerStatus]),
        [heartbeatStatus.status, networkStatus.status, powerStatus.status],
    );

    return <DeviceStatusContext.Provider value={status}>{children}</DeviceStatusContext.Provider>;
}

function WeatherStationStatusProvider({
    deviceId,
    children,
}: {
    deviceId: string;
    children: ReactNode;
}) {
    const heartbeatStatus = useHeartbeatStatus({ deviceId });
    const networkStatus = useNetworkStatus({ deviceId });
    const powerStatus = usePowerStatus({ deviceId });
    const weatherStatus = useWeatherStatus({ deviceId });

    const status = useMemo(
        () => aggregateStatusMessages([heartbeatStatus, networkStatus, powerStatus, weatherStatus]),
        [heartbeatStatus.status, networkStatus.status, powerStatus.status, weatherStatus.status],
    );

    return <DeviceStatusContext.Provider value={status}>{children}</DeviceStatusContext.Provider>;
}

function TankSensorStatusProvider({
    deviceId,
    children,
}: {
    deviceId: string;
    children: ReactNode;
}) {
    const heartbeatStatus = useHeartbeatStatus({ deviceId });
    const networkStatus = useNetworkStatus({ deviceId });
    const powerStatus = usePowerStatus({ deviceId });
    const tankSensorStatus = useTankSensorStatus({ deviceId });

    const status = useMemo(
        () =>
            aggregateStatusMessages([
                heartbeatStatus,
                networkStatus,
                powerStatus,
                tankSensorStatus,
            ]),
        [heartbeatStatus.status, networkStatus.status, powerStatus.status, tankSensorStatus.status],
    );

    return <DeviceStatusContext.Provider value={status}>{children}</DeviceStatusContext.Provider>;
}

function LocatorStatusProvider({ deviceId, children }: { deviceId: string; children: ReactNode }) {
    const locatorStatus = useLocatorStatus({ deviceId });

    const status = useMemo(() => locatorStatus, [locatorStatus.status]);

    return <DeviceStatusContext.Provider value={status}>{children}</DeviceStatusContext.Provider>;
}
